import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DefaultLogo from "../assets/images/logo.png";
import { colors, ReportColorStyle } from "../theme";
import { formatDate } from "../utils";
import { ReportOverrideFooter } from "../pages/EggFreezeCalculator/types";
import GlobeIcon from "../assets/images/report_icons/globe.png";
import LocationIcon from "../assets/images/report_icons/map.png";
import PhoneIcon from "../assets/images/report_icons/phone.png";
import { PPNeueMontrealMedium, PPNeueMontrealBold } from "./fonts";

interface PatientInformationType {
  rows: {
    columns: { key: string; value: string }[];
  }[];
}
class PDF {
  static pageMargins = { x: 30, y: 30 };
  static lineMargin = 15;
  private y: number = 0;
  private pdf: jsPDF | null = null;
  private logo: string | null = null;
  private date: Date | undefined;
  private patientInformation: PatientInformationType | undefined;
  private colorStyle: ReportColorStyle | undefined;
  private title: string = "";
  private pageSize: { width: number; height: number } = { width: 0, height: 0 };
  private logoSize: { width: number; height: number } = { width: 0, height: 0 };
  private containerWidth: number = 800;
  private footer: ReportOverrideFooter | undefined;
  private cellSpacingFactor: number = 1.2;

  constructor(
    colorStyle: ReportColorStyle,
    title: string,
    footer?: ReportOverrideFooter,
    containerWidth: number = 800
  ) {
    this.pdf = new jsPDF("p", "pt", "a4");
    this.initializeFonts();
    this.colorStyle = colorStyle;
    this.title = title;
    this.logo = DefaultLogo;
    this.pageSize = {
      width: this.pdf.internal.pageSize.getWidth(),
      height: this.pdf.internal.pageSize.getHeight(),
    };
    this.containerWidth = containerWidth;
    this.footer = footer;
  }

  public getPageSize(): { width: number; height: number } {
    return this.pageSize;
  }

  public getPDF(): jsPDF | null {
    return this.pdf;
  }

  private initializeFonts() {
    this.pdf!.addFileToVFS(
      "Inter_24pt-SemiBold-normal.ttf",
      PPNeueMontrealBold
    );
    this.pdf!.addFileToVFS("Inter_24pt-normal.ttf", PPNeueMontrealMedium);
    this.pdf!.addFont(
      "Inter_24pt-SemiBold-normal.ttf",
      "Inter_24pt-SemiBold",
      "semi-bold"
    );
    this.pdf!.addFont("Inter_24pt-normal.ttf", "Inter_24pt-normal", "normal");
  }

  public setLogo(
    logo: string | null,
    size?: { width: number; height: number } | null
  ) {
    this.logo = logo;
    if (size) {
      this.logoSize = size;
    }
  }

  public getY(): number {
    return this.y;
  }

  public setDate(date: Date) {
    this.date = date;
  }

  public setPatientInformation(
    patientInformation: PatientInformationType,
    cellSpacingFactor: number = 1.2
  ) {
    this.patientInformation = patientInformation;
    this.cellSpacingFactor = cellSpacingFactor;
  }

  async addTextWithIcon(
    title: string,
    content: string,
    icon: string,
    iconSize: number = 20,
    backgroundColor: string = "#E7E5E4",
    fontColor = "#000000"
  ) {
    const cardHeight = 100;
    let y = this.y + PDF.lineMargin;
    const width = this.pageSize.width - 2 * PDF.pageMargins.x;

    const prevFontSize = this.pdf!.getFontSize();
    const prevTextColor = this.pdf!.getTextColor();
    const prevFillColor = this.pdf!.getFillColor();
    const prevDrawColor = this.pdf!.getDrawColor();
    this.pdf!.setFontSize(14);
    this.pdf!.setTextColor("#000000");
    this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
    this.pdf!.text(title, PDF.pageMargins.x, y);
    this.pdf!.setLineHeightFactor(1.7);
    this.pdf!.setTextColor("#475569");
    this.pdf!.setFillColor(backgroundColor);
    y += PDF.lineMargin;
    // set the border color
    // this.pdf!.setDrawColor("#000000");
    // this.drawRoundedRect(PDF.pageMargins.x, y, width, cardHeight, 8);

    this.drawRoundedRect(
      PDF.pageMargins.x,
      y,
      width,
      cardHeight,
      8,
      colors.stone[100]
    );

    const iconX = PDF.pageMargins.x + PDF.lineMargin;
    const iconY = y + PDF.lineMargin;

    this.pdf!.addImage(icon, "PNG", iconX, iconY, iconSize, iconSize);

    this.pdf!.setFontSize(11);
    this.pdf!.setFont("Inter_24pt-normal", "normal");
    const infoText = content;
    const textX = iconX + iconSize + PDF.lineMargin;
    const textY = iconY;
    const textWidthInBox = width - iconSize - 30;
    this.pdf!.text(infoText, textX, textY + PDF.lineMargin, {
      maxWidth: textWidthInBox,
    });

    this.y = y + cardHeight;

    this.pdf!.setFontSize(prevFontSize);
    this.pdf!.setTextColor(prevTextColor);
    this.pdf!.setFillColor(prevFillColor);
    this.pdf!.setDrawColor(prevDrawColor);
  }

  public async addHtmlCanvas(
    htmlElement: HTMLElement,
    options = {},
    width: number | null = null,
    height: number | null = null,
    x?: number,
    y?: number
  ) {
    const canvas = await html2canvas(htmlElement, options);
    const aspectRatio = canvas.width / canvas.height;
    const imgData = canvas.toDataURL("image/png");

    const nWidth = width ? width : this.pageSize.width - 2 * PDF.pageMargins.x;
    // const nHeight = height
    //   ? height
    //   : (canvas.height * this.pageSize.width) / canvas.width;
    const nHeight = nWidth / aspectRatio;

    console.log(`canvas`, canvas.width, canvas.height, width, height);

    this.pdf!.addImage(
      imgData,
      "PNG",
      x ?? PDF.pageMargins.x,
      y ?? this.y,
      nWidth,
      nHeight
    );
    this.y += nHeight;
  }

  public setY(y: number) {
    this.y = y;
  }

  public addPage() {
    this.pdf!.addPage();
    this.y = 0;
    this.addHeader();
    this.addFooter();
  }

  public addSubTitle(subtitle: string, fontColor: string = "#000000") {
    this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
    this.addText(subtitle, "Inter_24pt-SemiBold", 16, fontColor);
    this.pdf!.setFont("Inter_24pt-normal", "normal");
  }

  public addText(
    text: string,
    fontFamily: string = "Inter_24pt-normal",
    fontSize: number = 12,
    color: string = "#000000"
  ) {
    const prevClr = this.pdf!.getTextColor();
    this.pdf!.setFontSize(fontSize);
    this.pdf!.setFont(fontFamily);
    this.pdf!.setTextColor(color);
    this.pdf!.text(text, PDF.pageMargins.x, this.y, {
      maxWidth: this.pageSize.width - 2 * PDF.pageMargins.x,
    });
    this.y += PDF.lineMargin;
    this.pdf!.setTextColor(prevClr);
  }

  public addMargins(margins: number) {
    this.y += margins * PDF.lineMargin;
  }

  private addFullWidthLine(
    lineWidth: number,
    color: string,
    withMargins: boolean = true
  ) {
    this.pdf!.setLineWidth(lineWidth);
    this.pdf!.setDrawColor(color);
    this.pdf!.line(
      withMargins ? PDF.pageMargins.x : 0,
      this.y,
      this.pageSize.width - (withMargins ? PDF.pageMargins.x : 0),
      this.y
    );
    this.y += PDF.lineMargin;
  }

  private addHeader() {
    this.addFullWidthLine(10, this.colorStyle?.main || "#000000", false);

    if (this.logo) {
      this.pdf!.addImage(
        this.logo,
        "PNG",
        PDF.pageMargins.x,
        PDF.pageMargins.y,
        this.logoSize.width,
        this.logoSize.height
      );
    }

    if (this.date) {
      const dateWidth =
        this.pdf!.getStringUnitWidth(`Date of test: ${formatDate(this.date)}`) *
        12;
      this.pdf!.setFontSize(12);
      this.pdf!.setTextColor("#000000");
      this.pdf!.text(
        `Date of test: ${formatDate(this.date)}`,
        this.pageSize.width - PDF.pageMargins.x - dateWidth,
        PDF.pageMargins.y + this.logoSize.height / 2 + 15
      );
    }

    this.y += 32 + this.logoSize.height + PDF.lineMargin;
    this.y += PDF.lineMargin;

    this.addFullWidthLine(1, "#CCCCCC");

    this.y += PDF.lineMargin;
  }

  private addTitle() {
    this.y += PDF.lineMargin;
    const prevFontSize = this.pdf!.getFontSize();
    this.pdf!.setFontSize(24);
    this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
    this.pdf!.setTextColor("#000000");
    this.pdf!.text(this.title, PDF.pageMargins.x, this.y);
    this.pdf!.getTextDimensions(this.title);
    this.y += PDF.lineMargin + this.pdf!.getTextDimensions(this.title).h;
    this.pdf!.setFont("Inter_24pt-normal", "normal");
    this.pdf!.setFontSize(prevFontSize);
  }

  private addPatientInformation() {
    if (!this.patientInformation) {
      return;
    }
    const cellWidth = 120;
    const borderRadius = 10;
    const maxLineWidth = cellWidth / this.cellSpacingFactor - 10; // Subtract padding

    this.addSubTitle("Patient Details");

    // First calculate total height needed including wrapped text
    let totalHeight = 20; // Initial padding
    this.patientInformation.rows.forEach((row) => {
      let maxLinesInRow = 1;
      row.columns.forEach((column) => {
        // Split text into lines that fit within maxLineWidth
        const lines = this.splitTextToLines(column.value, maxLineWidth);
        maxLinesInRow = Math.max(maxLinesInRow, lines.length);
      });
      totalHeight += maxLinesInRow * 20; // Height per line
    });

    // Draw background
    this.pdf!.setFillColor(this.colorStyle?.light || "#FFFFFF");
    this.drawRoundedRect(
      PDF.pageMargins.x,
      this.y,
      this.pageSize.width - 2 * PDF.pageMargins.x,
      totalHeight + PDF.lineMargin,
      borderRadius,
      colors.stone[100]
    );

    // Render text
    this.pdf!.setFontSize(12);
    this.pdf!.setFont("Inter_24pt-normal", "normal");
    this.y += PDF.lineMargin * 2;

    let currentY = this.y;

    this.patientInformation.rows.forEach((row) => {
      let rowStartY = currentY;
      let maxLinesInCurrentRow = 1;

      // First pass: calculate height needed for this row
      row.columns.forEach((column) => {
        const lines = this.splitTextToLines(column.value, maxLineWidth);
        maxLinesInCurrentRow = Math.max(maxLinesInCurrentRow, lines.length);
      });

      // Second pass: render the text
      let x = PDF.pageMargins.x + PDF.lineMargin;
      row.columns.forEach((column) => {
        // Render key
        this.pdf!.text(column.key, x, rowStartY);
        x += cellWidth / this.cellSpacingFactor;

        // Render value (potentially multiple lines)
        this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
        const lines = this.splitTextToLines(column.value, maxLineWidth);
        lines.forEach((line, lineIndex) => {
          this.pdf!.text(line, x, rowStartY + lineIndex * 20);
        });

        this.pdf!.setFont("Inter_24pt-normal", "normal");
        x += cellWidth / this.cellSpacingFactor;
      });

      currentY += maxLinesInCurrentRow * 20;
    });

    this.y = currentY;
  }

  private splitTextToLines(text: string, maxWidth: number): string[] {
    const words = text.toString().split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = this.pdf!.getTextWidth(testLine);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  // Helper function to draw a rounded rectangle
  private drawRoundedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    borderColor: string = "#000000"
  ) {
    const prevDrawColor = this.pdf!.getDrawColor();
    this.pdf!.setDrawColor(borderColor);
    this.pdf!.roundedRect(x, y, width, height, radius, radius, "D");
    this.pdf!.setDrawColor(prevDrawColor);
  }

  private addFooter() {
    if (!this.footer) {
      console.log("footer is not set");
      return;
    }

    const options = {
      backgroundColor: "#F5F5F5",
    };

    const iconSize = 20;

    this.pdf!.setFontSize(10);
    this.pdf!.setFont("Inter_24pt-normal", "normal");
    this.pdf!.setTextColor("#64748B");

    const addressSize = this.pdf!.getTextDimensions(this.footer.address);
    const phoneSize = this.pdf!.getTextDimensions(this.footer.phone);
    const websiteSize = this.pdf!.getTextDimensions(this.footer.website);
    let midX = this.pageSize.width / 2 - addressSize.w / 2 - iconSize - 10;

    this.pdf!.addImage(
      LocationIcon,
      "PNG",
      midX,
      this.pageSize.height - 40,
      iconSize,
      iconSize
    );

    // align text with icon
    this.pdf!.text(
      this.footer.address,
      midX + iconSize + 10,
      this.pageSize.height - 27
    );

    midX =
      (this.pageSize.width - (phoneSize.w + websiteSize.w + iconSize + 10)) /
        2 -
      35;

    this.pdf!.addImage(
      PhoneIcon,
      "PNG",
      midX,
      this.pageSize.height - 70,
      iconSize,
      iconSize
    );
    this.pdf!.text(
      this.footer.phone,
      midX + iconSize + 10,
      this.pageSize.height - 57
    );
    midX += phoneSize.w + 35;
    this.pdf!.addImage(
      GlobeIcon,
      "PNG",
      midX,
      this.pageSize.height - 70,
      iconSize,
      iconSize
    );
    this.pdf!.text(
      this.footer.website,
      midX + iconSize + 5,
      this.pageSize.height - 57
    );
  }

  public addCard(
    title: string,
    percentage: number,
    caption: string,
    cardWidth: number,
    cardHeight: number = 120,
    xOffset: number,
    backgroundColor: string = "#FFFFFF",
    borderColor: string = "#E5E5E5",
    fontColor: string = "#000000"
  ) {
    const cardX = PDF.pageMargins.x + xOffset; // Horizontal position based on xOffset
    const cardY = this.y; // Current vertical position

    // Draw card background with border
    this.drawRoundedRect(cardX, cardY, cardWidth, cardHeight, 8, borderColor);
    this.pdf!.setFillColor(backgroundColor);
    this.pdf!.rect(cardX, cardY, cardWidth, cardHeight, "F");

    // Add title
    this.pdf!.setFontSize(12);
    this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
    this.pdf!.setTextColor(fontColor);
    this.pdf!.text(title, cardX + 20, cardY + 30);

    // Add percentage
    this.pdf!.setFontSize(16);
    this.pdf!.setFont("Inter_24pt-SemiBold", "semi-bold");
    this.pdf!.text(`${percentage}%`, cardX + 20, cardY + 60);

    // Add caption
    this.pdf!.setFontSize(12);
    this.pdf!.setFont("Inter_24pt-normal", "normal");
    this.pdf!.setTextColor("#64748B"); // A lighter font color for the caption
    this.pdf!.text(caption, cardX + 20, cardY + 90);
  }

  public build() {
    this.addHeader();
    this.addTitle();
    this.addPatientInformation();
    this.addFooter();
  }

  public save(filename: string = "report.pfd") {
    this.pdf?.save(filename);
  }
}

export default PDF;
